import React, {
  FocusEventHandler,
  KeyboardEventHandler,
  useCallback,
  useRef,
  useState,
} from "react";
import { connect } from "react-redux";

import { faArrowsRotate } from "@fortawesome/pro-solid-svg-icons/faArrowsRotate";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, EmailSubject, GradientIcon } from "@mapmycustomers/ui";

import EmailContext from "@app/component/email/creation/EmailContext";
import LoadingDots from "@app/component/LoadingDots";
import UserSetting from "@app/enum/UserSetting";
import { generateEmailContent } from "@app/store/email/actions";
import { getUserSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import isEmailBodyEmpty from "@app/util/email/isEmailBodyEmpty";

import styles from "./AISubjectField.module.scss";

const messages = defineMessages({
  regenerateTooltip: {
    id: "email.creation.aiSubjectField.regenerate",
    defaultMessage: "New subject",
    description: "Tooltip for the AI subject regenerate button",
  },
});

interface AISubjectFieldProps {
  className?: string;
}

const AISubjectField = ({
  className,
  onGenerateEmailContent,
  signatureText,
}: AISubjectFieldProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const {
    aiSubjectSuggestion,
    body,
    dynamicVariables,
    entities,
    entityType,
    onAiSubjectSuggestionChange,
    onSubjectChange,
    subject,
  } = useNonNullableContext(EmailContext);

  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);

  const bodyRef = useRef(body);
  bodyRef.current = body;

  const subjectEmpty = !subject.trim();
  const bodyEmpty = isEmailBodyEmpty(body, signatureText);
  const showSuggestion = focused && subjectEmpty && !!aiSubjectSuggestion;
  const showLoader = focused && loading;
  const showAcceptButton = focused && subjectEmpty && !!aiSubjectSuggestion && !loading;
  const showRegeneratePill = focused && !subjectEmpty && !loading && !bodyEmpty;

  const handleGenerateSubject = useCallback(
    (regenerate: boolean) => {
      const requestedBody = body;
      setLoading(true);
      onGenerateEmailContent({
        callback: (response) => {
          setLoading(false);
          if (bodyRef.current === requestedBody && response.subject) {
            onAiSubjectSuggestionChange(response.subject);
          }

          if (regenerate) {
            onSubjectChange(response.subject);
          }
        },
        email: { body, subject: regenerate ? subject : "" },
        entityId: entities.length === 1 ? entities[0].id : undefined,
        entityType: entities.length === 1 ? entities[0].entity : undefined,
        failureCallback: () => {
          setLoading(false);
        },
        prompt: "",
        regenerate,
        subjectOnly: true,
      });
    },
    [body, onGenerateEmailContent, subject, entities, onAiSubjectSuggestionChange, onSubjectChange]
  );

  const handleFocus = useDynamicCallback<FocusEventHandler>(() => {
    setFocused(true);
    if (!subjectEmpty || bodyEmpty || loading || aiSubjectSuggestion) {
      return;
    }
    handleGenerateSubject(false);
  });

  const handleBlur = useDynamicCallback<FocusEventHandler>(() => {
    setFocused(false);
  });

  const handleKeyDown = useDynamicCallback<KeyboardEventHandler>((e) => {
    if (e.key === "Tab" && !e.shiftKey && showAcceptButton && aiSubjectSuggestion) {
      e.preventDefault();
      e.stopPropagation();
      onSubjectChange(aiSubjectSuggestion);
    }
  });

  const handleAccept = useDynamicCallback(() => {
    if (aiSubjectSuggestion) {
      onSubjectChange(aiSubjectSuggestion);
    }
  });

  const handleRegenerate = useDynamicCallback(() => {
    if (bodyEmpty) {
      return;
    }
    onAiSubjectSuggestionChange(undefined);
    handleGenerateSubject(true);
  });

  return (
    <div className={cn(styles.wrapper, className)} onKeyDownCapture={handleKeyDown}>
      <EmailSubject
        className={styles.subjectField}
        dynamicVariables={dynamicVariables}
        entityType={entityType}
        onBlur={handleBlur}
        onChange={onSubjectChange}
        onFocus={handleFocus}
        placeholder={showSuggestion ? aiSubjectSuggestion : undefined}
        value={subject}
      />
      <div className={styles.actionSlot}>
        {showLoader && <LoadingDots mini />}
        {showAcceptButton && (
          <Button
            icon={<GradientIcon className={styles.pillIcon} icon={faCheck} />}
            iconOnly
            onClick={handleAccept}
            onMouseDown={(e) => e.preventDefault()}
            size="small"
            type="ai"
          />
        )}
        {showRegeneratePill && (
          <Tooltip title={intl.formatMessage(messages.regenerateTooltip)}>
            <Button
              icon={<GradientIcon className={styles.pillIcon} icon={faArrowsRotate} />}
              iconOnly
              onClick={handleRegenerate}
              onMouseDown={(e) => e.preventDefault()}
              size="small"
              type="ai"
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const bulkEmailSetting = getUserSettingValue(state, UserSetting.BULK_EMAIL);
  return {
    signatureText: bulkEmailSetting?.signature ? (bulkEmailSetting?.signatureText ?? "") : "",
  };
};

const mapDispatchToProps = {
  onGenerateEmailContent: generateEmailContent.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AISubjectField);
